import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Eye, X } from 'lucide-react';
const NAMES = ["Juan", "María", "Carlos", "Ana", "Luis", "Elena", "Pedro", "Lucía", "Miguel", "Sofía", "Jorge", "Laura", "David", "Carmen", "Alejandro", "Isabel", "Daniel", "Marta", "José", "Paula", "Diego", "Valentina", "Andrés", "Camila", "Fernando", "Valeria"];
const CITIES = ["Miami, FL", "Los Angeles, CA", "New York, NY", "Houston, TX", "Chicago, IL", "Dallas, TX", "San Antonio, TX", "San Diego, CA", "Phoenix, AZ", "Austin, TX", "San Jose, CA", "Fort Worth, TX", "Jacksonville, FL", "Columbus, OH", "Charlotte, NC", "San Francisco, CA", "Indianapolis, IN", "Seattle, WA", "Denver, CO", "Washington, DC"];
interface Product {
  title: string;
  editionLabel: string;
  priceNumber: number;
  priceText: string;
  thumbnailUrl: string;
}
interface ToastData {
  id: number;
  name: string;
  city: string;
  product: Product;
}
export function SocialProofPopups() {
  // Viewer count state
  const [viewers, setViewers] = useState(47);

  // Toast state
  const [currentToast, setCurrentToast] = useState<ToastData | null>(null);
  const shouldReduceMotion = useReducedMotion();
  const usedNamesRef = useRef<string[]>([]);
  const productsRef = useRef<Product[]>([]);

  // 1. Live Viewer Count Logic
  useEffect(() => {
    const interval = setInterval(() => {
      setViewers(prev => {
        const change = Math.floor(Math.random() * 5) - 2; // -2 to +2
        const next = prev + change;
        return next < 24 ? 24 : next > 189 ? 189 : next;
      });
    }, 4500);
    return () => clearInterval(interval);
  }, []);

  // 2. DOM Scanner for Products
  useEffect(() => {
    const scanDOM = (): Product[] => {
      const found: Product[] = [];

      // Try to find the specific product on this page first
      const priceRegex = /\$\s?(\d+(\.\d{2})?)/;
      const textNodes: string[] = [];
      const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, null);
      let node;
      while (node = walker.nextNode()) {
        if (node.nodeValue && priceRegex.test(node.nodeValue)) {
          textNodes.push(node.nodeValue);
        }
      }

      // Heuristic: if we find $11.99, we know it's the Nano-Creative Bible
      const has1199 = textNodes.some(t => t.includes('11.99'));
      if (has1199) {
        found.push({
          title: "Nano-Creative Bible",
          editionLabel: "Acceso de por vida",
          priceNumber: 11.99,
          priceText: "$11.99",
          thumbnailUrl: "https://cdn.magicpatterns.com/uploads/rMdh97iSyvr22tFCGZvNWa/B-01.png"
        });
      }
      return found;
    };
    productsRef.current = scanDOM();
  }, []);

  // 3. Toast Timing & Randomization Loop
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    let isActive = true;
    const showToast = () => {
      if (!isActive) return;

      // Pick Product (from scanned DOM or fallback)
      let selectedProduct: Product;
      if (productsRef.current.length > 0) {
        selectedProduct = productsRef.current[Math.floor(Math.random() * productsRef.current.length)];
      } else {
        // Fallback distribution: 70% Pack Despertar, 30% Edición Digital
        const rand = Math.random();
        if (rand < 0.7) {
          selectedProduct = {
            title: "Pack Despertar",
            editionLabel: "Physical Book",
            priceNumber: 27,
            priceText: "$27.00",
            thumbnailUrl: "https://cdn.magicpatterns.com/uploads/rMdh97iSyvr22tFCGZvNWa/B-01.png"
          };
        } else {
          selectedProduct = {
            title: "Edición Digital",
            editionLabel: "Digital Edition",
            priceNumber: 17,
            priceText: "$17.00",
            thumbnailUrl: "https://cdn.magicpatterns.com/uploads/rMdh97iSyvr22tFCGZvNWa/B-01.png"
          };
        }
      }

      // Pick Name (avoiding last 10)
      let availableNames = NAMES.filter(n => !usedNamesRef.current.includes(n));
      if (availableNames.length === 0) {
        usedNamesRef.current = []; // reset if all used
        availableNames = NAMES;
      }
      const selectedName = availableNames[Math.floor(Math.random() * availableNames.length)];

      // Update rolling history
      usedNamesRef.current.push(selectedName);
      if (usedNamesRef.current.length > 10) {
        usedNamesRef.current.shift();
      }

      // Pick City
      const selectedCity = CITIES[Math.floor(Math.random() * CITIES.length)];
      setCurrentToast({
        id: Date.now(),
        name: selectedName,
        city: selectedCity,
        product: selectedProduct
      });

      // Hide after 4 seconds
      timeoutId = setTimeout(() => {
        if (isActive) {
          setCurrentToast(null);
          // Schedule next toast after 8-15 seconds
          const nextInterval = Math.floor(Math.random() * (15000 - 8000 + 1)) + 8000;
          timeoutId = setTimeout(showToast, nextInterval);
        }
      }, 4000);
    };

    // First toast appears after 3 seconds
    timeoutId = setTimeout(showToast, 3000);
    return () => {
      isActive = false;
      clearTimeout(timeoutId);
    };
  }, []);
  const animationProps = shouldReduceMotion ? {
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1
    },
    exit: {
      opacity: 0
    }
  } : {
    initial: {
      opacity: 0,
      y: 50
    },
    animate: {
      opacity: 1,
      y: 0
    },
    exit: {
      opacity: 0,
      y: 50
    }
  };
  return <>
      {/* Live Viewer Count */}
      <div className="fixed top-4 right-4 z-50 flex items-center gap-2 px-3 py-2 bg-zinc-900/90 backdrop-blur-md border border-zinc-800 rounded-full shadow-lg" data-id="element-162" data-mp="kjowqto73r3c">
        <div className="relative flex items-center justify-center w-5 h-5" data-id="element-163" data-mp="ph6xvxvz120s">
          <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-20 animate-ping" data-id="element-164" data-mp="358urpkfouefd"></span>
          <Eye className="relative w-4 h-4 text-red-500" data-id="element-165" data-mp="3aak4p5mh1263" />
        </div>
        <span className="text-sm font-medium text-zinc-300" data-id="element-166" data-mp="1gqjlxjwxx6vj">
          <strong className="text-white" data-id="element-167" data-mp="1br6z2z62jzsy">{viewers}</strong> visualizando ahora
        </span>
      </div>

      {/* Recent Purchase Toast */}
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-full max-w-sm px-4 pointer-events-none" data-id="element-168" data-mp="16zf5sgvj3fz4">
        <AnimatePresence mode="wait" data-id="element-169" data-mp="120kr9r4lgdkt">
          {currentToast && <motion.div key={currentToast.id} {...animationProps} transition={{
          duration: 0.4,
          ease: "easeOut"
        }} className="bg-zinc-900/95 backdrop-blur-md border border-zinc-800 rounded-2xl p-3 flex items-center gap-4 shadow-[0_10px_40px_rgba(0,0,0,0.5)] pointer-events-auto" data-id="element-170" data-mp="x2snnzul730s">
              <div className="w-12 h-12 shrink-0 rounded-xl overflow-hidden bg-zinc-800 border border-zinc-700" data-id="element-171" data-mp="j4cf03d5z2g0">
                <img src={currentToast.product.thumbnailUrl} alt={currentToast.product.title} className="w-full h-full object-cover" loading="lazy" decoding="async" data-id="element-172" data-mp="2id9jir6vibbo" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-173" data-mp="2px1xz0jxl3xc">
                <p className="text-sm text-zinc-400 leading-tight mb-0.5" data-id="element-174" data-mp="2upleo6dx0f6d">
                  <span className="text-white font-bold" data-id="element-175" data-mp="2zook3rxt4nev">{currentToast.name}</span> en {currentToast.city} acaba de comprar
                </p>
                <p className="text-sm font-medium text-amber-500 truncate" data-id="element-176" data-mp="222heceprop0x">
                  {currentToast.product.title} {currentToast.product.editionLabel ? `(${currentToast.product.editionLabel})` : ''} — {currentToast.product.priceText}
                </p>
              </div>
              <button onClick={() => setCurrentToast(null)} className="p-1 text-zinc-500 hover:text-zinc-300 transition-colors rounded-full hover:bg-zinc-800 shrink-0" aria-label="Cerrar notificación" data-id="element-177" data-mp="2gyqah7ed2m94">
                <X className="w-4 h-4" data-id="element-178" data-mp="hc9v2b20lm9b" />
              </button>
            </motion.div>}
        </AnimatePresence>
      </div>
    </>;
}